/**
 * @copyright
 * Copyright(c) 2020 SICK AG
 */

/**
 * A service that notifies registered listeners about theme changes.
 *
 * Theme changes are detected automatically but you can also invoke
 * {@link forceThemeCheck} to trigger a check manually at any time.
 */
export class ThemeService {
  constructor() {
    this._listeners = [];
    this._lastCheck = 0;
    this._checkInterval = 250;

    if ('requestIdleCallback' in window) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this._schedulingFunction = window['requestIdleCallback'];
      this._timeoutInMilliseconds = {
        timeout: this._checkInterval
      };
    } else {
      this._schedulingFunction = window.setTimeout;
      this._timeoutInMilliseconds = this._checkInterval;
    } // Make the forceThemeCheck function globally available through the window object.


    if (window['forceThemeCheck'] === undefined) {
      // eslint-disable-next-line
      // @ts-ignore
      window['forceThemeCheck'] = () => {
        ThemeService.GET_INSTANCE().forceThemeCheck();
      };
    } // The current implementation of detecting theme changes is too time consuming.
    // Therefore checks are disabled.
    // this._scheduleThemeCheck();

  } // This interval is used to throttle theme checks. It is the time that needs to be passed before a check is executed.


  /**
   * The time that needs to be passed before a theme check is executed.
   *
   * @default 250 milliseconds
   */
  get checkInterval() {
    return this._checkInterval;
  }

  set checkInterval(value) {
    this._checkInterval = value;

    if (typeof this._timeoutInMilliseconds === 'number') {
      this._timeoutInMilliseconds = this._checkInterval;
    } else {
      this._timeoutInMilliseconds.timeout = this._checkInterval;
    }
  }
  /**
   * Retrieve the global {@link ThemeService} instance
   */


  static GET_INSTANCE() {
    if (!ThemeService._instance) {
      ThemeService._instance = new ThemeService();
    }

    return ThemeService._instance;
  }

  _scheduleThemeCheck() {
    // The function must be called with 'window' being the current object!
    this._schedulingFunction.call(window, () => {
      this._checkCurrentTheme();
    }, this._timeoutInMilliseconds);
  }

  _checkCurrentTheme() {
    // DAVIDE-116: we assume at this point that a theme change has occurred and that
    // all listeners must recompute their styles.
    // An alternative approach could look like this: use a MutationObserver that observes
    // the root element for changes. Also listen for resize events on the window to handle
    // style-changes in combination with media queries.
    const now = Date.now();

    if (now - this._lastCheck > this._checkInterval) {
      this._lastCheck = now;

      this._notifyListeners();
    }

    this._scheduleThemeCheck();
  }
  /**
   * Adds the given listener to this service
   * @param listener - will be notified about theme changes
   * @see removeListener
   */


  addListener(listener) {
    this._listeners.push(listener);
  }
  /**
   * Removes the given listener from this service
   * @param listener - the listener to be removed
   * @see addListener
   */


  removeListener(listener) {
    for (let i = 0; i < this._listeners.length; i++) {
      if (this._listeners[i] === listener) {
        this._listeners.splice(i, 1);

        return;
      }
    }
  }
  /**
   * Notify all listeners about a theme change
   */


  _notifyListeners() {
    this._listeners.forEach(listener => {
      listener();
    });
  }
  /**
   * Call this function to force a theme check at anytime.
   */


  forceThemeCheck() {
    this._notifyListeners();
  }

}
